// src/pages/ClientPromotions.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Stack
} from '@mui/material';
import {
  CardGiftcard as GiftIcon,
  CheckCircle as ActiveIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  Tv as StreamingIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { get, post } from '../services/apiService';
import FiberLoader from '../components/common/FiberLoader';
import { useClientAuth } from '../contexts/ClientAuthContext';

const ClientPromotions = () => {
  const { client } = useClientAuth();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Claim states
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [promoToClaim, setPromoToClaim] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('clientToken');
      const response = await fetch('/api/client/promotions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar promociones');
      }

      const data = await response.json();
      setPromotions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching client promotions:', err);
      setError('No se pudieron cargar tus promociones en este momento.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimPromo = async () => {
    if (!promoToClaim) return;
    setClaimLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('clientToken');
      const response = await fetch(`/api/client/promotions/claim/${promoToClaim.client_promotion_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al reclamar promoción');
      }

      setSuccess(`¡Felicidades! Has activado la promoción "${promoToClaim.name}" con éxito.`);
      setClaimDialogOpen(false);
      setPromoToClaim(null);
      fetchPromotions();
    } catch (err) {
      console.error('Error claiming promotion:', err);
      setError(err.message || 'Error al reclamar la promoción.');
    } finally {
      setClaimLoading(false);
    }
  };

  const offeredPromos = promotions.filter(p => p.status === 'offered');
  const activePromos = promotions.filter(p => p.status === 'active');

  if (loading) {
    return (
      <FiberLoader text="Buscando promociones exclusivas de fibra óptica..." subtext="Cargando ofertas y beneficios de tu plan..." fullPage />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GiftIcon sx={{ fontSize: 40, color: '#6366f1' }} />
          Promociones y Regalos
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
          Gracias por tu puntualidad. Aquí tienes las ofertas exclusivas y premios de fidelización disponibles para ti.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{success}</Alert>}

      {/* Offered Promotions Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#fbbf24' }} />
          Promociones Disponibles
        </Typography>
        
        {offeredPromos.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
            <GiftIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1.5 }} />
            <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">No tienes ofertas pendientes</Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              Mantente al día con tus pagos antes de la fecha límite de corte para calificar a futuros premios de fidelidad.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {offeredPromos.map((promo) => (
              <Grid item xs={12} sm={6} md={4} key={promo.client_promotion_id}>
                <Card sx={{
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(99, 102, 241, 0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fefeff 100%)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(99, 102, 241, 0.12)',
                    borderColor: 'rgba(99, 102, 241, 0.3)'
                  }
                }}>
                  {/* Decorative color strip */}
                  <Box sx={{
                    height: '6px',
                    background: promo.type === 'discount' ? 'linear-gradient(90deg, #10b981, #059669)' :
                               promo.type === 'speed_upgrade' ? 'linear-gradient(90deg, #6366f1, #4f46e5)' :
                               promo.type === 'speed_and_free_service' ? 'linear-gradient(90deg, #8b5cf6, #ec4899)' :
                               promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'linear-gradient(90deg, #f59e0b, #ec4899)' :
                               'linear-gradient(90deg, #f59e0b, #d97706)'
                  }} />
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Box sx={{
                        p: 1,
                        borderRadius: '10px',
                        bgcolor: promo.type === 'discount' ? 'rgba(16, 185, 129, 0.1)' :
                                 promo.type === 'speed_upgrade' ? 'rgba(99, 102, 241, 0.1)' :
                                 promo.type === 'speed_and_free_service' ? 'rgba(139, 92, 246, 0.1)' :
                                 promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'rgba(236, 72, 153, 0.1)' :
                                 'rgba(245, 158, 11, 0.1)',
                        color: promo.type === 'discount' ? '#10b981' :
                               promo.type === 'speed_upgrade' ? '#6366f1' :
                               promo.type === 'speed_and_free_service' ? '#8b5cf6' :
                               promo.type === 'plan_upgrade_with_discount_and_free_service' ? '#ec4899' :
                               '#f59e0b',
                        display: 'flex'
                      }}>
                        {promo.type === 'discount' ? <MoneyIcon /> :
                         promo.type === 'speed_upgrade' ? <SpeedIcon /> :
                         promo.type === 'speed_and_free_service' ? <AutoAwesomeIcon /> :
                         promo.type === 'plan_upgrade_with_discount_and_free_service' ? <AutoAwesomeIcon /> :
                         <StreamingIcon />}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                        {promo.name}
                      </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ color: '#475569', minHeight: '50px', lineHeight: 1.5, mb: 3 }}>
                      {promo.description}
                    </Typography>

                    <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} />

                    {/* Promo Detail Box */}
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                      {promo.type === 'discount' && (
                        <>
                          <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.25rem' }}>
                            S/ {parseFloat(promo.discount_amount || 0).toFixed(2)}
                            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>/ mes desc.</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                            Por una duración de <strong>{promo.discount_months} meses</strong>
                          </Typography>
                        </>
                      )}

                      {promo.type === 'speed_upgrade' && (
                        <>
                          <Typography variant="h6" sx={{ color: '#6366f1', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.25rem' }}>
                            Doble Velocidad
                            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}> (+S/ {parseFloat(promo.speed_upgrade_price_addon || 0).toFixed(2)}/mes)</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                            Multiplica tu velocidad actual por <strong>{parseFloat(promo.speed_upgrade_multiplier || 0).toFixed(1)}</strong>
                          </Typography>
                        </>
                      )}

                      {promo.type === 'free_service' && (
                        <>
                          <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.25rem' }}>
                            {promo.service_name}
                            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}> Gratis</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                            Valorado en <strong>S/ {parseFloat(promo.service_price || 0).toFixed(2)}</strong> (se descontará el 100%)
                          </Typography>
                        </>
                      )}

                      {promo.type === 'speed_and_free_service' && (
                        <>
                          <Typography variant="h6" sx={{ color: '#6366f1', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.25rem' }}>
                            Doble Velocidad
                            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}> (+S/ {parseFloat(promo.speed_upgrade_price_addon || 0).toFixed(2)}/mes)</span>
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.25rem', mt: 1.5 }}>
                            {promo.service_name}
                            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}> Gratis</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                            Valorado en <strong>S/ {parseFloat(promo.service_price || 0).toFixed(2)}</strong> (se descontará el 100%)
                          </Typography>
                        </>
                      )}

                      {promo.type === 'plan_upgrade_with_discount_and_free_service' && (() => {
                        const planPrice = parseFloat(promo.target_plan_price || 0);
                        const servicePrice = parseFloat(promo.service_price || 0);
                        const realTotal = planPrice + servicePrice;
                        const discount = parseFloat(promo.discount_amount || 0);
                        const promoPrice = Math.max(0, realTotal - discount);
                        return (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                              Detalles del beneficio:
                            </Typography>
                            
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Plan {promo.target_plan_name || 'Superior'}:</Typography>
                              <Typography variant="caption" color="textPrimary" fontWeight="bold">S/ {planPrice.toFixed(2)}</Typography>
                            </Stack>
                            
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Servicio {promo.service_name || 'Regalo'}:</Typography>
                              <Typography variant="caption" color="textPrimary" fontWeight="bold">S/ {servicePrice.toFixed(2)} (Gratis)</Typography>
                            </Stack>
                            
                            <Divider sx={{ my: 0.5 }} />
                            
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Precio Real del Servicio:</Typography>
                              <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ textDecoration: 'line-through' }}>
                                S/ {realTotal.toFixed(2)}
                              </Typography>
                            </Stack>
                            
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Precio Total de Promoción:</Typography>
                              <Typography variant="subtitle2" color="success.main" fontWeight="800">
                                S/ {promoPrice.toFixed(2)} / mes
                              </Typography>
                            </Stack>
                            
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">Duración de la Promoción:</Typography>
                              <Typography variant="caption" color="textPrimary" fontWeight="bold">
                                {promo.discount_months} meses
                              </Typography>
                            </Stack>
                            
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5, borderTop: '1px dashed #e2e8f0', pt: 0.5 }}>
                              *Después se cobrará el precio real del servicio de S/ {realTotal.toFixed(2)}/mes.
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        setPromoToClaim(promo);
                        setClaimDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1.2,
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: 'none',
                        backgroundColor: promo.type === 'discount' ? '#10b981' :
                                         promo.type === 'speed_upgrade' ? '#6366f1' :
                                         promo.type === 'speed_and_free_service' ? '#8b5cf6' :
                                         promo.type === 'plan_upgrade_with_discount_and_free_service' ? '#ec4899' :
                                         '#f59e0b',
                        '&:hover': {
                          backgroundColor: promo.type === 'discount' ? '#059669' :
                                           promo.type === 'speed_upgrade' ? '#4f46e5' :
                                           promo.type === 'speed_and_free_service' ? '#7c3aed' :
                                           promo.type === 'plan_upgrade_with_discount_and_free_service' ? '#db2777' :
                                           '#d97706',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {promo.type === 'discount' ? 'Reclamar Descuento' :
                       promo.type === 'speed_upgrade' ? 'Activar Doble Velocidad' :
                       promo.type === 'speed_and_free_service' ? 'Activar Combo Regalo' :
                       promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'Reclamar Upgrade de Plan' :
                       'Reclamar Regalo'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Active Promotions Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ActiveIcon sx={{ color: '#10b981' }} />
          Mis Promociones Activas
        </Typography>

        {activePromos.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: '12px', bgcolor: 'transparent', border: '1px solid #e2e8f0', p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No tienes promociones activas actualmente en tu servicio.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {activePromos.map((promo) => (
              <Grid item xs={12} sm={6} key={promo.client_promotion_id}>
                <Paper sx={{
                  p: 3,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5
                }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: 'rgba(16, 185, 129, 0.08)',
                    color: '#10b981',
                    display: 'flex'
                  }}>
                    <ActiveIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                      {promo.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      Tipo: {
                        promo.type === 'discount' ? 'Descuento Mensual' :
                        promo.type === 'speed_upgrade' ? 'Aumento de Velocidad' :
                        promo.type === 'speed_and_free_service' ? 'Velocidad + Streaming Gratis' :
                        promo.type === 'plan_upgrade_with_discount_and_free_service' ? 'Upgrade Plan + Descuento + Streaming' : 'Streaming Gratis'
                      }
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mt: 0.5, display: 'block' }}>
                      Activa desde: {new Date(promo.activated_at).toLocaleDateString('es-ES')}
                    </Typography>
                  </Box>
                  <Chip
                    label="ACTIVO"
                    color="success"
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: '6px' }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Claim Dialog */}
      <Dialog
        open={claimDialogOpen}
        onClose={() => !claimLoading && setClaimDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          Confirmar Activación de Promoción
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569', mb: 2 }}>
            ¿Estás seguro de que deseas reclamar la promoción <strong>"{promoToClaim?.name}"</strong>?
          </DialogContentText>
          
          <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
              Detalles del Beneficio:
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.5 }}>
              {promoToClaim?.description}
            </Typography>
            
            {(promoToClaim?.type === 'speed_upgrade' || promoToClaim?.type === 'speed_and_free_service') && (
              <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <Typography variant="caption" sx={{ color: '#1e3a8a', fontWeight: 'bold', display: 'block' }}>
                  ⚡ Nota sobre Velocidad:
                </Typography>
                <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', mt: 0.2 }}>
                  Esta promoción duplicará la velocidad en tu router de forma inmediata y agregará un concepto adicional de S/ {promoToClaim?.speed_upgrade_price_addon.toFixed(2)} a tus próximas facturas.
                </Typography>
              </Box>
            )}
            {promoToClaim?.type === 'plan_upgrade_with_discount_and_free_service' && (() => {
              const planPrice = parseFloat(promoToClaim?.target_plan_price || 0);
              const servicePrice = parseFloat(promoToClaim?.service_price || 0);
              const realTotal = planPrice + servicePrice;
              const discount = parseFloat(promoToClaim?.discount_amount || 0);
              const promoPrice = Math.max(0, realTotal - discount);
              return (
                <Box sx={{ mt: 1.5, p: 2, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    🚀 Resumen del Cambio a Contratar:
                  </Typography>
                  <Stack spacing={0.5} sx={{ color: '#14532d', fontSize: '0.85rem' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <span>Plan {promoToClaim?.target_plan_name || 'Superior'} (Precio Real):</span>
                      <strong>S/ {planPrice.toFixed(2)}/mes</strong>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <span>Servicio {promoToClaim?.service_name || 'Regalo'} (Precio Real):</span>
                      <strong>S/ {servicePrice.toFixed(2)}/mes (Gratis)</strong>
                    </Stack>
                    <Divider sx={{ my: 0.5, borderColor: '#bbf7d0' }} />
                    <Stack direction="row" justifyContent="space-between" sx={{ fontSize: '0.9rem' }}>
                      <span>Precio Real Total del Servicio:</span>
                      <span style={{ textDecoration: 'line-through' }}>S/ {realTotal.toFixed(2)}/mes</span>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#166534' }}>
                      <span>Monto Mensual Promocional:</span>
                      <span>S/ {promoPrice.toFixed(2)}/mes</span>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <span>Duración del descuento:</span>
                      <strong>{promoToClaim?.discount_months} meses</strong>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#166534', fontStyle: 'italic', display: 'block', mt: 1.5, borderTop: '1px dashed #bbf7d0', pt: 1 }}>
                    *Al aceptar, su plan cambiará. Pagará S/ {promoPrice.toFixed(2)}/mes por {promoToClaim?.discount_months} meses. Luego de ese periodo, se le cobrará la tarifa real de S/ {realTotal.toFixed(2)}/mes.
                  </Typography>
                </Box>
              );
            })()}
            {(promoToClaim?.type === 'free_service' || promoToClaim?.type === 'speed_and_free_service') && (
              <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                <Typography variant="caption" sx={{ color: '#78350f', fontWeight: 'bold', display: 'block' }}>
                  📺 Nota sobre Streaming Gratis:
                </Typography>
                <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mt: 0.2 }}>
                  Se agregará el servicio de streaming ({promoToClaim?.service_name}) a tu cuenta a su costo regular de S/ {promoToClaim?.service_price ? promoToClaim?.service_price.toFixed(2) : '0.00'}, aplicando un descuento correspondiente del 100% en tu facturación mensual de forma recurrente.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setClaimDialogOpen(false)}
            disabled={claimLoading}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleClaimPromo}
            variant="contained"
            disabled={claimLoading}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: 3.5,
              fontWeight: 700,
              backgroundColor: promoToClaim?.type === 'discount' ? '#10b981' :
                               promoToClaim?.type === 'speed_upgrade' ? '#6366f1' :
                               promoToClaim?.type === 'speed_and_free_service' ? '#8b5cf6' :
                               promoToClaim?.type === 'plan_upgrade_with_discount_and_free_service' ? '#ec4899' :
                               '#f59e0b',
              '&:hover': {
                backgroundColor: promoToClaim?.type === 'discount' ? '#059669' :
                                 promoToClaim?.type === 'speed_upgrade' ? '#4f46e5' :
                                 promoToClaim?.type === 'speed_and_free_service' ? '#7c3aed' :
                                 promoToClaim?.type === 'plan_upgrade_with_discount_and_free_service' ? '#db2777' :
                                 '#d97706'
              }
            }}
          >
            {claimLoading ? <CircularProgress size={20} color="inherit" /> : '¡Confirmar y Activar!'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientPromotions;
